import { Controller } from "@hotwired/stimulus"

// The Pripomienky list on Automatické e-maily: add, remove, renumber, and a
// client-side mirror of PR-4 so a bad row says so before the round trip. The
// server is the authority — everything here is feedback, never a gate.
export default class extends Controller {
  static targets = ["form", "list", "template", "addButton", "count", "error"]
  static values = {
    max: Number,
    countTemplate: String,
    countMaxTemplate: String,
    orderMessage: String,
    unsavedMessage: String,
  }

  #dirty = false
  // Strong parameters only keep a fields_for-style hash under keys matching
  // /\A-?\d+\z/ — a "new1758…" key is dropped in silence and the row never
  // reaches the server. Digits only, and monotonic so two adds inside one
  // millisecond cannot share a key.
  #nextIndex = Date.now()

  connect() {
    document.addEventListener("turbo:before-visit", this.#onBeforeVisit)
    window.addEventListener("beforeunload", this.#onBeforeUnload)
    this.renumber()
  }

  disconnect() {
    document.removeEventListener("turbo:before-visit", this.#onBeforeVisit)
    window.removeEventListener("beforeunload", this.#onBeforeUnload)
  }

  toggle(event) {
    const checkbox = event.currentTarget
    const card = checkbox.closest(".card")
    if (!card) return

    card.classList.toggle("document-emails__card--muted", !checkbox.checked)
  }

  // Keeps the row's [data-enabled] in step with its switch so the dimmed state
  // is visible immediately, not only after a save and reload.
  toggleRule(event) {
    const row = event.currentTarget.closest("[data-rule]")
    if (!row) return

    row.dataset.enabled = event.currentTarget.checked ? "true" : "false"
  }

  add() {
    if (this.#rows().length >= this.maxValue) return

    // A timestamp, not the row count: an index a removed row still holds would
    // make two rows post under the same key and the later one would win.
    // The fragment is cloned and its attributes rewritten rather than the
    // template's innerHTML string-replaced — no HTML is ever parsed here.
    const fragment = this.templateTarget.content.cloneNode(true)
    const index = String(this.#nextIndex++)
    for (const element of fragment.querySelectorAll("[name], [id]")) {
      if (element.name) element.name = element.name.replaceAll("__INDEX__", index)
      if (element.id) element.id = element.id.replaceAll("__INDEX__", index)
    }
    this.listTarget.appendChild(fragment)
    this.#dirty = true
    this.renumber()
    this.validate()
  }

  // A saved row is not deleted from the DOM — it is hidden and its _destroy
  // flipped, because only a posted row can tell the server to destroy it.
  remove(event) {
    const row = event.currentTarget.closest("[data-rule]")
    if (!row) return

    if (row.querySelector("[data-rule-id]")) {
      row.querySelector("[data-rule-destroy]").value = "1"
      row.hidden = true
    } else {
      row.remove()
    }
    this.#dirty = true
    this.renumber()
    this.validate()
  }

  edited() {
    this.#dirty = true
    this.validate()
  }

  // The submit takes the draft with it, so leaving afterwards is not a loss.
  saving() {
    this.#dirty = false
  }

  renumber() {
    const rows = this.#rows()
    rows.forEach((row, index) => {
      const number = row.querySelector("[data-rule-no]")
      if (number) number.textContent = `${index + 1}`
    })
    if (this.hasAddButtonTarget) this.addButtonTarget.disabled = rows.length >= this.maxValue
    if (this.hasCountTarget) {
      this.countTarget.textContent =
        rows.length >= this.maxValue && this.hasCountMaxTemplateValue
          ? this.countMaxTemplateValue
          : this.countTemplateValue.replace("%COUNT%", String(rows.length))
    }
  }

  // PR-4, mirrored: whole numbers 1–365, no two rows of one relation sharing an
  // offset, and each row later in time than the one before it.
  validate() {
    const offending = this.#offendingRows()
    this.#rows().forEach((row) => {
      row.classList.toggle("document-emails__rule--invalid", offending.has(row))
    })
    if (!this.hasErrorTarget) return

    this.errorTarget.hidden = offending.size === 0
    if (offending.size > 0) this.errorTarget.textContent = this.orderMessageValue
  }

  #offendingRows() {
    const offending = new Set()
    const previous = new Map()
    for (const row of this.#rows()) {
      const days = Number(row.querySelector("[data-rule-days]").value)
      const relation = row.querySelector("[data-rule-relation]").value
      if (!Number.isInteger(days) || days < 1 || days > 365) offending.add(row)
      if (!this.#followsPrevious(previous.get(relation), days, relation)) offending.add(row)
      if (Number.isInteger(days)) previous.set(relation, days)
    }
    return offending
  }

  // Within one relation the offsets ascend in TIME: after-due offsets grow,
  // before-due offsets shrink, and neither may repeat.
  #followsPrevious(previous, days, relation) {
    if (previous === undefined || !Number.isInteger(days)) return true

    return relation === "before_due" ? days < previous : days > previous
  }

  #rows() {
    return [...this.listTarget.querySelectorAll("[data-rule]")].filter((row) => !row.hidden)
  }

  // Arrow-function fields, not methods: they are added and removed as document
  // and window listeners, so each needs one stable bound identity.
  #onBeforeVisit = (event) => {
    if (!this.#dirty) return
    if (!window.confirm(this.unsavedMessageValue)) event.preventDefault()
  }

  // A reload or a closed tab loses the draft as completely as a Turbo visit.
  // The browser shows its own wording; cancelling the event is all we control.
  #onBeforeUnload = (event) => {
    if (!this.#dirty) return

    event.preventDefault()
  }
}
